import React, { useState, useEffect, useContext } from 'react';
import { Input } from '../../components/Input';
import { PageFormsData, PageProps } from '../../types/hexpertTypes';
import { Hexpert as endpoints } from '@services/endpoints';
import Axios from 'axios';
import { Checkbox } from '@components/Checkbox';
import { Button } from '../../components/Buttons';
import HEXLongArrowLeft from '@icons/hexpert/HEXLongArrowLeft';
import { HexpertContext } from '../../contexts/HexPertProvider';
import dataLayer from '@helpers/dataLayer';
import './HexpertRequestQuoteBlock.scss';
import { generateTransactionId } from '@helpers/generateTransactionId';

enum FormFields {
  FORM_GUID = '__FormGuid',
  FORM_LANGUAGE = '__FormLanguage',
  FORM_HOSTED_PAGE = '__FormHostedPage',
  FORM_ANTI_FORGERY = '__RequestVerificationToken',
}

interface IProps {
  form: PageFormsData;
  guid: string;
  shareUrl?: string;
  title?: string;
  pageProps: PageProps;
  onClose: () => void;
}

export const HexpertRequestQuoteBlock = ({
  form,
  guid,
  shareUrl,
  title,
  onClose,
  pageProps,
}: IProps) => {
  const [busy, setBusy] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [data, setData] = useState<{ [key: string]: string }>();
  const { state, dispatch } = useContext(HexpertContext);
  const [errorMessages, setErrorMessages] = useState<{
    [key: string]: string;
  }>();

  // Text fields that should be set automatically and thus not be visible
  const hiddenFields = ['ShareUrl'];

  useEffect(() => {
    if (!(shareUrl && shareUrl?.length > 0)) {
      const noResultTitle = state?.pageProps?.NoResultTitle;
      dataLayer.push('page_view', {
        page_path: `${state?.hexpertSteps?.join('/')}/${noResultTitle}`,
        page_title: `Hexpert-${state?.currentForm?.title} - ${noResultTitle}`,
        page_category: [...(state?.hexpertSteps || []), noResultTitle],
        product_name: noResultTitle,
        transaction_id: generateTransactionId(true, state?.currentForm?.id),
      });
    }
  }, [])

  useEffect(() => {
    // Create fields in our state
    if (window) {
      window.scrollTo(0, 0);
    }
    if (form?.Value?.ElementsArea?.length > 0) {
      // Got some form elements. Iterate and fill
      let d = { ...data };
      for (let element in form.Value.ElementsArea) {
        // Autmatically set the share url
        if (form.Value.ElementsArea[element].FieldName === 'ShareUrl') {
          d[form.Value.ElementsArea[element].FieldId] = shareUrl;
        } else {
          d[form.Value.ElementsArea[element].FieldId] =
            form.Value.ElementsArea[element].PredefinedValue;
        }
      }
      setData(d);
    }
  }, [form]);

  const submit = async () => {
    setBusy(true);
    // Get antiforgery
    const antiforgery = await Axios.get(endpoints.getAntiForgeryToken);

    // Create form data
    const formData = new FormData();
    formData.append(FormFields.FORM_GUID, guid);
    formData.append(FormFields.FORM_ANTI_FORGERY, antiforgery.data);
    formData.append(FormFields.FORM_LANGUAGE, window['lang']);

    // Add all the entered data to the form data
    for (let key in data) {
      if (data[key]) formData.append(key, data[key]);
    }

    // Submit the form
    const result = await Axios.post(endpoints.submitHexpertForm, formData, {
      headers: {
        ['antiforgerytoken']: antiforgery.data,
      },
    });

    if (result.data.IsSuccess) {
      const resultTitle =
        state?.productResult?.ProductDetails?.Title || state?.pageProps?.NoResultTitle;
      const formTitle = form?.Value?.Title;
      dataLayer.push('hexpert_form_submit', {
        page_path: `${state?.hexpertSteps?.join('/')}/${resultTitle}/${formTitle}`,
        page_title: `Hexpert-${state?.currentForm?.title} - ${formTitle}`,
        page_category: [...(state?.hexpertSteps || []), resultTitle, formTitle],
        product_name: resultTitle,
        transaction_id: generateTransactionId(false, state?.currentForm?.id),
      });
    }
    setBusy(false);
    // If submit failed, show error messages and return
    if (!result.data.IsSuccess) {
      let messages: { [key: string]: string } = {};
      for (var message of result.data.AdditionalParams.__FormField) {
        messages[message.InvalidElementName] = message.ValidationMessage;
      }
      setErrorMessages(messages);
      return;
    }
    if (!shareUrl || shareUrl?.length < 1) {
      dispatch({ type: 'resetStepper' });
    } else {
      closePage();
    }
  };

  // Reset entered data and close page
  const closePage = () => {
    setShowConfirm(false);
    setData({});
    onClose();
  };
  // Generic function to set form field values
  const onChange = (name: string, value: string) => {
    setData((prev) => ({ ...prev, ...{ [name]: value } }));
  };

  // Handle checkbox toggle
  const toggleCheck = (name: string) => {
    setData((prev) => ({
      ...prev,
      ...{ [name]: prev?.[name] === 'true' ? '' : 'true' },
    }));
  };

  return (
    <>
      <div className='c-hex-request-quote-back-arrow-container'>
        <div className='c-hex-request-quote-back-arrow' onClick={() => onClose()}>
          <HEXLongArrowLeft />
        </div>
      </div>

      <div className='c-hex-result-page'>
        <div
          className={'c-hex-quote-form' + (shareUrl && shareUrl?.length > 0 ? ' has-quote' : '')}
        >
          {shareUrl && shareUrl?.length > 0 ? (
            <>
              <div className='c-hex-quote-section'>
                <div className='c-hex-quote-section-spacer' />
                <div>
                  <div className='c-hex-quote-title'>{title}</div>
                </div>
              </div>
              <div className='c-hex-quote-section'>
                <div className='c-hex-quote-badge-container'>
                  <div className='c-hex-quote-badge'>1</div>
                  <div className='c-hex-quote-badge-line' />
                </div>
                <div className='c-hex-quote-elements'>
                  <div className='c-hex-quote-title'>{pageProps.EnquireTitle}</div>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: pageProps.EnquireDescription,
                    }}
                  ></div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className='c-hex-quote-no-result-title'>{pageProps.NoResultTitle}</div>
              {pageProps.NoResultDescription && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: pageProps.NoResultDescription,
                  }}
                ></div>
              )}
            </>
          )}
          <div className='c-hex-quote-section'>
            {shareUrl && (
              <div className='c-hex-quote-badge-container'>
                <div className='c-hex-quote-badge'>2</div>
                <div className='c-hex-quote-badge-line' />
              </div>
            )}
            <div>
              <div className='c-hex-quote-elements'>
                {shareUrl && (
                  <div className='c-hex-quote-title'>{pageProps.PersonalInformationTitle}</div>
                )}
                {form?.Value?.ElementsArea.filter(
                  (element) => hiddenFields.indexOf(element.FieldName) < 0
                ).map((element) => {
                  return (
                    <>
                      {element.ContentType === 'TextboxElementBlock' && (
                        <div>
                          <p>
                            {element.Label}
                            {element.Validators?.indexOf('RequiredValidator') >= 0 ? ' *' : ''}
                          </p>
                          <Input
                            placeHolder={element.Label}
                            onChange={(ev) => onChange(element.FieldId, ev.target.value)}
                          />
                          {errorMessages?.[element.FieldId] && (
                            <div className='c-hex-input-error'>
                              {errorMessages?.[element.FieldId]}
                            </div>
                          )}
                        </div>
                      )}
                      {element.ContentType === 'TextareaElementBlock' && (
                        <div>
                          <p>
                            {element.Label}
                            {element.Validators?.indexOf('RequiredValidator') >= 0 ? ' *' : ''}
                          </p>
                          <textarea
                            placeholder={element.Label}
                            className={'c-hex-quote-textarea'}
                            rows={8}
                            onChange={(ev) => {
                              onChange(element.FieldId, ev.target.value);
                            }}
                          />
                          {errorMessages?.[element.FieldId] && (
                            <div className='c-hex-input-error'>
                              {errorMessages?.[element.FieldId]}
                            </div>
                          )}
                        </div>
                      )}
                      {element.ContentType === 'HexpertCountryChoiceElementBlock' && (
                        <div>
                          <p>
                            {element.Label}
                            {element.Validators?.indexOf('RequiredValidator') >= 0 ? ' *' : ''}
                          </p>
                          <select
                            className={'c-hex-quote-dropdown'}
                            onChange={(ev) => {
                              onChange(element.FieldId, ev.target.value);
                            }}
                          >
                            <option value=''>{element.Label}</option>
                            {element.List.map((item, i) => (
                              <option key={i} value={`${item.Country}:${item.Email}`}>
                                {item.Country}
                              </option>
                            ))}
                          </select>
                          {errorMessages?.[element.FieldId] && (
                            <div className='c-hex-input-error'>
                              {errorMessages?.[element.FieldId]}
                            </div>
                          )}
                        </div>
                      )}

                      {element.ContentType === 'ChoiceElementBlock' && (
                        <div className='c-hex-checkbox'>
                          <Checkbox
                            id={element.FieldId}
                            name={element.FieldId}
                            label={element.Label}
                            checked={data?.[element.FieldId] === 'true'}
                            onChange={(e) => toggleCheck(element.FieldId)}
                          />
                          <span className='c-input-label'>{element.Label}</span>
                          {errorMessages?.[element.FieldId] && (
                            <div className='c-hex-input-error'>
                              {errorMessages?.[element.FieldId]}
                            </div>
                          )}
                        </div>
                      )}
                      {element.ContentType === 'SubmitButtonElementBlock' && (
                        <div>
                          <Button action={() => submit()} label={element.Label} busy={busy} />
                        </div>
                      )}
                    </>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        {(!shareUrl || shareUrl?.length < 1) && (
          <div>
            <Button
              action={() => {
                dispatch({ type: 'resetStepper' });
              }}
              label={pageProps.NoResultButtonLabel}
              busy={busy}
            />
          </div>
        )}
      </div>
    </>
  );
};
